/**
 * Shared cascading math logic for invoice/inventory recalculations.
 * Used by AuditPanel (local state) and InventoryTable (on-save DB update).
 */

export interface ItemMathFields {
  cantidad?: number | string | null;
  precio_unitario?: number | string | null;
  sub_total?: number | string | null;
  peso_bruto_kg?: number | string | null;
}

/** Recalculate sub_total = cantidad * precio_unitario */
export function calcSubTotal(cantidad: string | number | null | undefined, precio: string | number | null | undefined): number {
  return (parseFloat(String(cantidad)) || 0) * (parseFloat(String(precio)) || 0);
}

/** Aggregate items → parent document header totals
 *  total_factura = Σ(sub_total) + costo_flete + seguro + impuestos - descuento_ajuste
 */
export function calcHeaderTotals(items: ItemMathFields[], costoFlete: number, impuestos: number, descuentoAjuste: number = 0, seguro: number = 0, otrosCargos: number = 0) {
  let totalPiezas = 0;
  let pesoBruto = 0;
  let sumSubtotal = 0;

  for (const item of items) {
    totalPiezas += parseFloat(String(item.cantidad)) || 0;
    pesoBruto += parseFloat(String(item.peso_bruto_kg)) || 0;
    sumSubtotal += parseFloat(String(item.sub_total)) || 0;
  }

  return {
    total_piezas: totalPiezas,
    peso_bruto_kg: pesoBruto,
    total_factura: sumSubtotal + costoFlete + seguro + impuestos + otrosCargos - descuentoAjuste,
  };
}

/**
 * ⚠️ Legacy v1 landed cost engine REMOVED.
 *
 * The deprecated `calcLandedCosts` function and its `LandedCostResult` type
 * were purged in favor of the canonical engine at `src/lib/landedCostEngine.ts`
 * (`calcLandedCostV2`), accessed via the adapter `calcShipmentLandedCost` in
 * `src/hooks/useShipmentLandedCost.ts`.
 *
 * All consumers — `DashboardKPIs.tsx`, `InventoryTable.tsx`, and
 * `EmbarqueDetailModal.tsx` — now use the v2 engine, which guarantees:
 *   - Decimal.js precision (no floating-point drift)
 *   - Largest Remainder Method allocation (penny-perfect reconciliation)
 *   - IVA cascade `(CIF + Arancel) × IVA%`
 *   - TLC override (arancel = 0)
 *   - `aplica_costeo_inventario` and `trm_pago` honored per gasto
 *   - 1:1 parity with the SQL RPC `liquidar_y_cerrar_embarque` (engine_version='v2')
 */

